import React, { useState, useMemo, useCallback } from 'react';
import { Plus, Download, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UpgradePrompt } from './UpgradePrompt';
import { useCollaborationToast } from '../hooks/useCollaborationToast';
import useCollaborationPermissions from '../hooks/useCollaborationPermissions';
import PrimaryButton from './themed/PrimaryButton';
import ThemedCard from './themed/ThemedCard';
import CollaborationToast from './CollaborationToast';
import InvitationContextBanner from './InvitationContextBanner';
import { TableLayoutProps } from './table-layout/types';
import { useSeatingData } from './table-layout/hooks/useSeatingData';
import { useDragDrop } from './table-layout/hooks/useDragDrop';
import { useNotification } from './table-layout/hooks/useNotification';
import { TableSummaryBar } from './table-layout/components/TableSummaryBar';
import { TableCard } from './table-layout/components/TableCard';
import { UnassignedGuestList } from './table-layout/components/UnassignedGuestList';
import { NotificationPanel } from './table-layout/components/NotificationPanel';

export { type TableLayoutProps } from './table-layout/types';

export const TableLayout: React.FC<TableLayoutProps> = ({
  confirmedAttendees = [],
  inviteCode = 'default',
  refreshTrigger,
  currentInvitation,
}) => {
  const { user } = useAuth();
  const { messages, showCollaborationAlert, dismissToast } = useCollaborationToast();
  const { canEdit, loading: permissionsLoading } = useCollaborationPermissions(
    currentInvitation?.id || null
  );

  const [editingTable, setEditingTable] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    tables,
    setTables,
    guests,
    loading,
    lastSyncedAt,
    defaultCapacity,
    setDefaultCapacity,
    stats,
    saveSeatingArrangement,
    handleRefresh,
    addNewTable,
    deleteTable,
    updateTableName,
    removeGuestFromTable,
    getLatestGuestCount,
    getLatestGuestGroup,
    exportSeatingArrangement,
    syncBlocked,
    syncReady,
    loadError,
    syncError,
    retrySync,
    retryLoad,
  } = useSeatingData({
    inviteCode,
    userId: user?.id,
    confirmedAttendees,
    refreshTrigger,
    showCollaborationAlert,
  });

  const editingDisabled = syncBlocked || !syncReady;

  const { limits, canAddTable, canExport } = useSubscription();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  const handleAddNewTable = useCallback(() => {
    if (!canAddTable(currentInvitation, tables.length)) {
      setUpgradeMessage(`您的方案最多支援 ${limits.maxTables} 桌座位安排。升級方案以增加桌數。`);
      setShowUpgradePrompt(true);
      return;
    }
    addNewTable();
  }, [canAddTable, currentInvitation, tables.length, addNewTable, limits]);

  const handleExportSeating = useCallback(() => {
    if (!canExport(currentInvitation)) {
      setUpgradeMessage('匯出功能需要標準方案或以上。升級方案以使用匯出功能。');
      setShowUpgradePrompt(true);
      return;
    }
    exportSeatingArrangement();
  }, [canExport, currentInvitation, exportSeatingArrangement]);

  const {
    handleDragStart,
    handleTableGuestDragStart,
    handleDragOver,
    handleDrop,
    handleDropToGuestArea,
  } = useDragDrop({ tables, setTables, saveSeatingArrangement, editingDisabled });

  const {
    notificationLoading,
    showNotificationModal,
    setShowNotificationModal,
    sendingProgress,
    handleTableNotification,
    handleSendNotifications,
  } = useNotification({
    inviteCode,
    userId: user?.id,
    tables,
    setTables,
    saveSeatingArrangement,
    editingDisabled,
  });

  const filteredGuests = useMemo(
    () =>
      guests.filter(
        guest =>
          guest.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !tables.some(table => table.guests.some(g => g.id === guest.id))
      ),
    [guests, searchQuery, tables]
  );

  const handleUpdateTableName = (tableId: number, newName: string) => {
    updateTableName(tableId, newName);
    setEditingTable(null);
  };

  return (
    <div className="space-y-6">
      <InvitationContextBanner invitation={currentInvitation || null} />

      {permissionsLoading && (
        <div className="text-center py-4">
          <div className="text-wedding-text-body">檢查權限中...</div>
        </div>
      )}

      {!permissionsLoading && !canEdit && (
        <ThemedCard className="p-6 text-center">
          <div className="text-wedding-text-body">
            您沒有權限管理此邀請函的座位安排。請聯絡邀請函擁有者。
          </div>
        </ThemedCard>
      )}

      {canEdit && (
        <>
          <TableSummaryBar stats={stats} tableCount={tables.length} />

          {syncBlocked && (
            <ThemedCard className="p-4 border-red-300 bg-red-50">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-medium text-red-800">雲端儲存失敗，已停止編輯</h3>
                  <p className="text-sm text-red-700 mt-1">
                    座位安排尚未成功同步到雲端。為避免您繼續編輯卻無法保存，系統已暫時停止修改功能。
                  </p>
                  {syncError && (
                    <p className="text-xs text-red-600 mt-1 font-mono">{syncError}</p>
                  )}
                </div>
                <PrimaryButton
                  onClick={retrySync}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 flex-shrink-0"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  重試同步
                </PrimaryButton>
              </div>
            </ThemedCard>
          )}

          {!syncReady && !loading && (
            <ThemedCard className="p-4 border-amber-300 bg-amber-50">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-medium text-amber-800">無法連線雲端，資料可能不是最新</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    目前顯示的是本機快取資料。在成功連線雲端前，編輯功能已暫時停用。
                  </p>
                  {loadError && (
                    <p className="text-xs text-amber-600 mt-1 font-mono">{loadError}</p>
                  )}
                </div>
                <PrimaryButton
                  onClick={retryLoad}
                  disabled={loading}
                  className="bg-amber-600 hover:bg-amber-700 flex-shrink-0"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  重試載入
                </PrimaryButton>
              </div>
            </ThemedCard>
          )}

          <div className={editingDisabled ? 'opacity-50 pointer-events-none' : ''}>
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <h2 className="text-xl font-semibold text-wedding-text-title">座位安排</h2>
                {lastSyncedAt && (
                  <span className="text-sm text-wedding-text-body">
                    最後同步: {lastSyncedAt.toLocaleTimeString('zh-TW')}
                  </span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-wedding-text-title">預設容量:</label>
                  <select
                    value={defaultCapacity}
                    onChange={e => setDefaultCapacity(parseInt(e.target.value))}
                    className="border border-wedding-pink/30 rounded px-2 py-1 text-sm"
                  >
                    <option value={4}>4人桌</option>
                    <option value={6}>6人桌</option>
                    <option value={8}>8人桌</option>
                    <option value={10}>10人桌</option>
                    <option value={12}>12人桌</option>
                  </select>
                </div>

                <div className="flex space-x-2">
                  <PrimaryButton
                    onClick={handleRefresh}
                    disabled={loading}
                    className="flex items-center space-x-2 bg-slate-500 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    <span>{loading ? '刷新中...' : '刷新資料'}</span>
                  </PrimaryButton>
                  <PrimaryButton
                    onClick={handleExportSeating}
                    className="flex items-center space-x-2 bg-wedding-gold hover:bg-wedding-gold/90"
                  >
                    <Download className="h-4 w-4" />
                    <span>匯出座位表</span>
                  </PrimaryButton>
                  <NotificationPanel
                    tables={tables}
                    loading={loading}
                    notificationLoading={notificationLoading}
                    showNotificationModal={showNotificationModal}
                    setShowNotificationModal={setShowNotificationModal}
                    sendingProgress={sendingProgress}
                    assignedGuests={stats.assignedGuests}
                    onTableNotification={handleTableNotification}
                    onSendNotifications={handleSendNotifications}
                  />
                  <PrimaryButton
                    onClick={handleAddNewTable}
                    className="flex items-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>新增桌次</span>
                  </PrimaryButton>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {tables.map(table => (
                    <TableCard
                      key={table.id}
                      table={table}
                      editingTable={editingTable}
                      setEditingTable={setEditingTable}
                      getLatestGuestCount={getLatestGuestCount}
                      getLatestGuestGroup={getLatestGuestGroup}
                      onUpdateTableName={handleUpdateTableName}
                      onDeleteTable={deleteTable}
                      onRemoveGuest={removeGuestFromTable}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onTableGuestDragStart={handleTableGuestDragStart}
                    />
                  ))}
                </div>
              </div>
              <UnassignedGuestList
                filteredGuests={filteredGuests}
                allGuestsCount={guests.length}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDropToGuestArea={handleDropToGuestArea}
              />
            </div>
          </div>
        </>
      )}

      <CollaborationToast messages={messages} onDismiss={dismissToast} />

      <UpgradePrompt
        open={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        message={upgradeMessage}
      />
    </div>
  );
};
